import {
  addDoc,
  collection,
  doc,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  limit,
} from 'firebase/firestore';
import { categoryLabel, type TicketValues } from '@sf/shared';
import { db } from '../../lib/firebase';
import { uploadImage } from '../../lib/storageImages';
import type { User } from '../../lib/types';

export const ticketRef = (id: string) => doc(db, 'tickets', id);
export const ticketMessagesQuery = (id: string) =>
  query(collection(db, 'tickets', id, 'messages'), orderBy('createdAt', 'asc'), limit(500));
export const tenantTicketsQuery = (uid: string) =>
  query(collection(db, 'tickets'), where('tenantId', '==', uid), orderBy('createdAt', 'desc'));
export const unitTicketsQuery = (unitId: string) =>
  query(collection(db, 'tickets'), where('unitId', '==', unitId), orderBy('createdAt', 'desc'));
/** Admin list: one building, so we load recent tickets and filter on the client (decision D16). */
export const allTicketsQuery = () =>
  query(collection(db, 'tickets'), orderBy('createdAt', 'desc'), limit(1000));

export async function createTicket(profile: User, values: TicketValues): Promise<string> {
  const newRef = doc(collection(db, 'tickets'));
  const title =
    values.category === 'other'
      ? values.title!.trim()
      : values.title?.trim() || categoryLabel(values.category);
  await setDoc(newRef, {
    tenantId: profile.id,
    tenantName: profile.name,
    unitId: profile.unitNumber,
    unitNumber: profile.unitNumber,
    category: values.category,
    title,
    description: values.description,
    imagePaths: [],
    status: 'open',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastMessageAt: serverTimestamp(),
    unreadForAdmin: 0,
    unreadForTenant: 0,
  });
  return newRef.id;
}

export function uploadTicketImage(
  ticketId: string,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<string> {
  return uploadImage(`tickets/${ticketId}`, file, onProgress);
}

export async function setTicketImages(ticketId: string, imagePaths: string[]): Promise<void> {
  await updateDoc(ticketRef(ticketId), { imagePaths, updatedAt: serverTimestamp() });
}

export async function sendTicketMessage(
  ticketId: string,
  profile: User,
  text: string,
  imagePaths: string[] = [],
): Promise<void> {
  await addDoc(collection(db, 'tickets', ticketId, 'messages'), {
    senderId: profile.id,
    senderRole: profile.role,
    text: text.trim(),
    type: 'text',
    ...(imagePaths.length ? { imagePaths } : {}),
    createdAt: serverTimestamp(),
  });
}

export async function markTicketRead(ticketId: string, role: User['role']): Promise<void> {
  await updateDoc(
    ticketRef(ticketId),
    role === 'admin'
      ? { adminLastReadAt: serverTimestamp(), unreadForAdmin: 0 }
      : { tenantLastReadAt: serverTimestamp(), unreadForTenant: 0 },
  );
}
